import { supabase } from '../config/supabase-clean';
import { convertToISO } from '../utils/dateUtils';

// Re-export supabase for use in other parts of the app
export { supabase };

/**
 * Generate a unique confirmation number
 * Format: YYYYMMDD-XXXX (e.g., 20260107-A1B2)
 */
export const generateConfirmationNumber = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  
  return `${year}${month}${day}-${random}`;
};

/**
 * Helper function to convert date to ISO format or null
 * Handles both MM/DD/YYYY and YYYY-MM-DD formats
 * @param {string} dateValue - Date value
 * @returns {string|null} - ISO date or null
 */
const formatDateForDB = (dateValue) => {
  if (!dateValue || dateValue === '') return null;
  
  // If it's already in ISO format (YYYY-MM-DD), return it
  if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return dateValue;
  }
  
  // If it's in MM/DD/YYYY format, convert it
  if (dateValue.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
    const iso = convertToISO(dateValue);
    return iso || null;
  }
  
  // If it's incomplete or invalid, return null
  return null;
};

/**
 * Submit tax form data to Supabase
 * @param {Object} formData - Complete form data
 * @param {string} language - Current language (en/es)
 * @returns {Object} - { success, data, error }
 */
export const submitTaxForm = async (formData, language = 'en') => {
  try {
    // Generate confirmation number
    const confirmationNumber = generateConfirmationNumber();
    
    // Prepare data for database (map formData keys to database columns)
    const dbData = {
      confirmation_number: confirmationNumber,
      language: language,
      status: 'new', // Set initial status to 'new'
      organization_id: formData.organization_id, // Multi-tenant support
      
      // Page 1: Taxpayer Information
      taxpayer_first_name: formData.taxpayerFirstName,
      taxpayer_middle_name: formData.taxpayerMiddleName,
      taxpayer_last_name: formData.taxpayerLastName,
      date_of_birth: formatDateForDB(formData.dateOfBirth),
      filing_status: formData.filingStatus,
      income_source: formData.incomeSource,
      street_address: formData.streetAddress,
      street_address_2: formData.streetAddress2,
      city: formData.city,
      state: formData.state,
      postal_code: formData.postalCode,
      phone_number: formData.phoneNumber,
      email: formData.email,
      ssn: formData.ssn,
      drivers_license: formData.driversLicense,
      id_expiration_date: formatDateForDB(formData.idExpirationDate),
      has_marketplace_insurance: formData.hasMarketplaceInsurance,
      wants_health_insurance: formData.wantsHealthInsurance,
      has_life_insurance: formData.hasLifeInsurance,
      wants_life_insurance: formData.wantsLifeInsurance,
      home_ownership: formData.homeOwnership,
      wants_to_buy_home: formData.wantsToBuyHome,
      
      // Spouse Information
      spouse_first_name: formData.spouseFirstName,
      spouse_middle_name: formData.spouseMiddleName,
      spouse_last_name: formData.spouseLastName,
      spouse_date_of_birth: formatDateForDB(formData.spouseDateOfBirth),
      spouse_ssn: formData.spouseSsn,
      spouse_drivers_license: formData.spouseDriversLicense,
      spouse_id_expiration_date: formatDateForDB(formData.spouseIdExpirationDate),
      
      // Dependents
      claiming_dependents: formData.claimingDependents,
      number_of_dependents: formData.numberOfDependents ? parseInt(formData.numberOfDependents) : null,
      dependents: (formData.dependents || []).map(dep => ({
        ...dep,
        dateOfBirth: formatDateForDB(dep.dateOfBirth)
      })),
      
      // Page 2: Qualifying Dependents
      all_dependents_citizens: formData.allDependentsCitizens,
      only_person_claiming: formData.onlyPersonClaiming,
      filing_joint_return: formData.filingJointReturn,
      related_to_you: formData.relatedToYou,
      meets_age_requirement: formData.meetsAgeRequirement,
      lives_with_you: formData.livesWithYou,
      financially_support: formData.financiallySupport,
      has_utility_bill: formData.hasUtilityBill,
      dependent_income_less_than_4200: formData.dependentIncomeLessThan4200,
      child_care_expenses: formData.childCareExpenses,
      
      // Page 3: Household Employee
      household_employee_acknowledged: formData.householdEmployeeAcknowledged || false,
      record_keeping_acknowledged: formData.recordKeepingAcknowledged || false,
      
      // Page 4: Business Expenses
      business_expenses: formData.scheduleC || {},
      
      // Page 5: Fees
      fees_name: formData.feesName,
      fees_ssn: formData.feesSsn,
      
      // Page 6: Disclosure
      disclosure_taxpayer_first_name: formData.disclosureTaxpayerFirstName,
      disclosure_taxpayer_middle_name: formData.disclosureTaxpayerMiddleName,
      disclosure_taxpayer_last_name: formData.disclosureTaxpayerLastName,
      signature_date: formatDateForDB(formData.signatureDate),
    };
    
    // Insert into database
    const { data, error } = await supabase
      .from('tax_forms')
      .insert([dbData])
      .select()
      .single();
    
    if (error) {
      console.error('Error submitting form:', error);
      return { success: false, error: error.message };
    }
    
    console.log('Form submitted successfully:', data);
    return { success: true, data, confirmationNumber };
    
  } catch (error) {
    console.error('Exception submitting form:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Upload a file to Supabase Storage
 * @param {File} file - File object to upload
 * @param {string} formId - UUID of the associated form
 * @param {Object} formData - Form data containing client name
 * @param {string} confirmationNumber - Confirmation number
 * @param {string} bucket - Storage bucket name (default: 'documents')
 * @returns {Object} - { success, data, error }
 */
export const uploadFile = async (file, formId, formData, confirmationNumber, bucket = 'documents') => {
  try {
    // Create human-readable folder name: LastName_FirstName_ConfirmationNumber
    const lastName = (formData.taxpayerLastName || formData.disclosureTaxpayerLastName || 'Unknown').replace(/[^a-zA-Z0-9]/g, '');
    const firstName = (formData.taxpayerFirstName || formData.disclosureTaxpayerFirstName || '').replace(/[^a-zA-Z0-9]/g, '');
    const folderName = `${lastName}_${firstName}_${confirmationNumber}`;
    
    // Create unique file path
    const timestamp = Date.now();
    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const filePath = `${folderName}/${timestamp}_${sanitizedFileName}`;
    
    // Upload file to storage
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });
    
    if (uploadError) {
      console.error('Error uploading file:', uploadError);
      return { success: false, error: uploadError.message };
    }
    
    // Record file metadata in database
    const { data: dbData, error: dbError } = await supabase
      .from('uploaded_documents')
      .insert([{
        form_id: formId,
        file_name: file.name,
        file_path: filePath,
        file_size: file.size,
        file_type: file.type
      }])
      .select()
      .single();
    
    if (dbError) {
      console.error('Error recording file metadata:', dbError);
      return { success: false, error: dbError.message };
    }
    
    console.log('File uploaded successfully:', filePath);
    return { success: true, data: { uploadData, dbData }, filePath };
    
  } catch (error) {
    console.error('Exception uploading file:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Upload PDF to Supabase Storage
 * @param {Blob} pdfBlob - PDF blob
 * @param {string} formId - UUID of the associated form
 * @param {Object} formData - Form data containing client name
 * @param {string} confirmationNumber - Confirmation number
 * @param {string} fileName - Name for the PDF file
 * @param {string} bucket - Storage bucket name (default: 'pdfs')
 * @returns {Object} - { success, data, error }
 */
export const uploadPDF = async (pdfBlob, formId, formData, confirmationNumber, fileName, bucket = 'pdfs') => {
  try {
    // Create human-readable folder name: LastName_FirstName_ConfirmationNumber
    const lastName = (formData.taxpayerLastName || formData.disclosureTaxpayerLastName || 'Unknown').replace(/[^a-zA-Z0-9]/g, '');
    const firstName = (formData.taxpayerFirstName || formData.disclosureTaxpayerFirstName || '').replace(/[^a-zA-Z0-9]/g, '');
    const folderName = `${lastName}_${firstName}_${confirmationNumber}`;
    
    // Create file path
    const filePath = `${folderName}/${fileName}`;
    
    // Upload PDF to storage
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(filePath, pdfBlob, {
        contentType: 'application/pdf',
        cacheControl: '3600',
        upsert: false
      });
    
    if (uploadError) {
      console.error('Error uploading PDF:', uploadError);
      return { success: false, error: uploadError.message };
    }
    
    // Record PDF metadata in database
    const { data: dbData, error: dbError } = await supabase
      .from('pdf_records')
      .insert([{
        form_id: formId,
        pdf_path: filePath,
        pdf_size: pdfBlob.size
      }])
      .select()
      .single();
    
    if (dbError) {
      console.error('Error recording PDF metadata:', dbError);
      return { success: false, error: dbError.message };
    }
    
    console.log('PDF uploaded successfully:', filePath);
    return { success: true, data: { uploadData, dbData }, filePath };
    
  } catch (error) {
    console.error('Exception uploading PDF:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Complete submission workflow:
 * 1. Submit form data
 * 2. Upload all documents
 * 3. Upload PDF (optional - can be done separately)
 * @param {Object} formData - Complete form data
 * @param {Array} files - Array of File objects
 * @param {Blob} pdfBlob - PDF blob (optional)
 * @param {string} pdfFileName - Name for PDF file (optional)
 * @param {string} language - Current language
 * @returns {Object} - { success, confirmationNumber, formId, filePaths, error }
 */
export const completeSubmission = async (formData, files, pdfBlob, pdfFileName, language) => {
  try {
    console.log('Starting complete submission...');
    
    // Step 1: Submit form data
    const formResult = await submitTaxForm(formData, language);
    if (!formResult.success) {
      return { success: false, error: 'Failed to submit form: ' + formResult.error };
    }
    
    const formId = formResult.data.id;
    const confirmationNumber = formResult.confirmationNumber;
    console.log('Form submitted, ID:', formId, 'Confirmation:', confirmationNumber);
    
    // Step 2: Upload all documents (if any) and collect file paths
    const filePaths = [];
    if (files && files.length > 0) {
      console.log(`Uploading ${files.length} documents...`);
      for (const file of files) {
        const fileResult = await uploadFile(file, formId, formData, confirmationNumber);
        if (fileResult.success && fileResult.filePath) {
          filePaths.push(fileResult.filePath);
          console.log(`Uploaded: ${fileResult.filePath}`);
        } else {
          console.error('Failed to upload file:', file.name, fileResult.error);
          // Continue with other files even if one fails
        }
      }
      console.log('Documents uploaded');
    }
    
    // Step 3: Upload PDF (if provided)
    if (pdfBlob && pdfFileName) {
      console.log('Uploading PDF...');
      const pdfResult = await uploadPDF(pdfBlob, formId, formData, confirmationNumber, pdfFileName);
      if (!pdfResult.success) {
        console.error('Failed to upload PDF:', pdfResult.error);
        // Continue anyway - form is submitted
      } else {
        console.log('PDF uploaded');
      }
    } else {
      console.log('PDF upload skipped (will be uploaded separately)');
    }
    
    console.log('Complete submission finished successfully');
    return { success: true, confirmationNumber, formId, filePaths };
    
  } catch (error) {
    console.error('Exception in complete submission:', error);
    return { success: false, error: error.message };
  }
};
